import { useState } from 'react';
import { MapPin, X, Check, Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { MARKER_STYLE } from '@/components/scout/BlockingDiagram';
import { SIMULATOR } from '@/constants/testIds';

const KINDS = ['parking', 'meeting', 'restroom', 'tree', 'water', 'power', 'hazard', 'other'];

/** Pin toolbar: pick a kind (+ optional label), then tap any diagram to place. Existing pins
 *  are listed as chips — click to highlight, rename inline, or remove. */
export const PinToolbar = ({ markers, placing, onPlacingChange, label, onLabelChange, selectedId, onSelect, onRemove, onRename }) => {
  const [renaming, setRenaming] = useState(null); // {id, value}

  return (
    <div data-testid={SIMULATOR.pinToolbar} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] inline-flex items-center gap-1.5 mr-1"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Pin a landmark</span>
        {KINDS.map((k) => {
          const st = MARKER_STYLE[k];
          const active = placing === k;
          return (
            <button
              key={k}
              type="button"
              data-testid={SIMULATOR.pinKind(k)}
              onClick={() => onPlacingChange(active ? null : k)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${active ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-fg))]' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))]/50'}`}
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[9px] font-bold" style={{ color: st.color, borderColor: st.color }}>{st.glyph}</span>
              {st.label}
            </button>
          );
        })}
        {placing && (
          <>
            <Input value={label} onChange={(e) => onLabelChange(e.target.value)} placeholder="Label (optional)" data-testid={SIMULATOR.pinLabelInput} className="h-7 w-44 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]" />
            <button type="button" onClick={() => onPlacingChange(null)} data-testid={SIMULATOR.pinCancel} className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]"><X className="h-3.5 w-3.5" /> Cancel</button>
          </>
        )}
      </div>
      {placing && (
        <p data-testid={SIMULATOR.pinPlacingHint} className="text-[11px] text-[hsl(var(--lxra-gold))]">Tap anywhere on a diagram to drop the {MARKER_STYLE[placing].label.toLowerCase()} pin. It appears on every moment and in Shoot Mode.</p>
      )}
      {markers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {markers.map((m) => {
            const st = MARKER_STYLE[m.kind] || MARKER_STYLE.other;
            const sel = selectedId === m.id;
            if (renaming?.id === m.id) {
              return (
                <form key={m.id} onSubmit={(e) => { e.preventDefault(); onRename(m.id, renaming.value.trim()); setRenaming(null); }} className="inline-flex items-center gap-1">
                  <Input autoFocus value={renaming.value} onChange={(e) => setRenaming({ id: m.id, value: e.target.value })} className="h-7 w-40 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]" />
                  <button type="submit" className="text-[hsl(var(--lxra-gold))]"><Check className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => setRenaming(null)} className="text-[hsl(var(--lxra-muted))]"><X className="h-3.5 w-3.5" /></button>
                </form>
              );
            }
            return (
              <span key={m.id} data-testid={SIMULATOR.pinChip(m.id)} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${sel ? 'border-[hsl(var(--lxra-gold))] text-[hsl(var(--lxra-fg))]' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]'}`}>
                <button type="button" onClick={() => onSelect(sel ? null : m.id)} className="inline-flex items-center gap-1.5">
                  <span className="font-bold text-[10px]" style={{ color: st.color }}>{st.glyph}</span>
                  {m.label || st.label}
                </button>
                <button type="button" onClick={() => setRenaming({ id: m.id, value: m.label || '' })} data-testid={SIMULATOR.pinRename(m.id)} title="Rename" className="hover:text-[hsl(var(--lxra-gold))]"><Pencil className="h-3 w-3" /></button>
                <button type="button" onClick={() => onRemove(m.id)} data-testid={SIMULATOR.pinRemove(m.id)} title="Remove pin" className="hover:text-red-400"><X className="h-3 w-3" /></button>
              </span>
            );
          })}
          <span className="text-[10px] text-[hsl(var(--lxra-muted))] ml-1">Drag a pin on any diagram to move it.</span>
        </div>
      )}
    </div>
  );
};
